import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { loadChampionships, ringCountsByManager, ringYearsByManager } from '../utils/championships';
import { managerSlug } from '../utils/slug';
import { ACTIVE_MEMBERS, FORMER_MEMBERS, isFormerMember } from '../utils/managers';
import { loadCareerRankings, normalizeManagerName } from '../utils/managerStats';
import { loadRoasts, roastsByCategories } from '../utils/roasts';
import Eyebrow from '../components/ui/Eyebrow';
import HeroBackground from '../components/ui/HeroBackground';
import ManagerAvatar from '../components/ui/ManagerAvatar';
import StatBlock from '../components/ui/StatBlock';
import { CastFigure, Reveal } from '../motion';

export default function Champions() {
  const [championships, setChampionships] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [almanacFacts, setAlmanacFacts] = useState([]);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    loadChampionships().then(setChampionships).catch(() => setChampionships([]));
    loadCareerRankings().then(setRankings).catch(() => setRankings([]));
    loadRoasts()
      .then((roasts) => setAlmanacFacts(roastsByCategories(roasts, [
        { category: 'dynasty_facts', label: 'Dynasty', limit: 2 },
        { category: 'drought_facts', label: 'Drought', limit: 2 },
        { category: 'chokes', label: 'Choke', limit: 2 },
      ])))
      .catch(() => setAlmanacFacts([]));
  }, []);

  const normalizedChampionships = championships.map((championship) => ({
    ...championship,
    champion: normalizeManagerName(championship.champion),
  }));
  const visibleYears = showAll ? normalizedChampionships : normalizedChampionships.slice(0, 4);

  const ringCounts = ringCountsByManager(normalizedChampionships);
  const ringYears = ringYearsByManager(normalizedChampionships);
  const uniqueWinners = Object.keys(ringCounts).length;
  const ringless = ACTIVE_MEMBERS.filter((m) => !ringCounts[m]);
  const defending = normalizedChampionships[0]; // sorted desc by year

  // For drought board years-of-waiting:
  const seasonsByManager = (name) => {
    const r = rankings.find((x) => x.manager === name);
    return r?.stats.seasons_played || 13;
  };

  return (
    <>
      {/* === HERO === */}
      <HeroBackground variant="gold" backdrop="studio">
        <div className="relative px-6 md:px-14 py-20 text-center overflow-hidden">
          <div className="relative">
            <Eyebrow>THE CHAMPIONS WALL</Eyebrow>
            <h1
              className="font-display font-black uppercase leading-[0.88] tracking-normal mt-6 mb-5"
              style={{ fontSize: 'clamp(48px, 8vw, 104px)' }}
            >
              {championships.length} trophies.<br /><span className="text-accent-gold">{uniqueWinners} winners.</span>
            </h1>
            <p className="text-base text-ink-secondary leading-relaxed max-w-2xl mx-auto mb-9 font-light">
              Fourteen managers have played in this league. <strong className="text-ink-primary font-bold">{ACTIVE_MEMBERS.length + FORMER_MEMBERS.length - uniqueWinners}</strong> of them have never lifted the trophy. A new season starts soon.
            </p>
            <div className="flex flex-wrap justify-center gap-12 border-y border-border py-6 max-w-3xl mx-auto">
              <StatBlock value={championships.length} label="SEASONS COMPLETED" color="gold" size="md" />
              <StatBlock value={uniqueWinners} label="UNIQUE WINNERS" color="gold" size="md" />
              <StatBlock value="2" label="DYNASTY ERAS" color="gold" size="md" />
              <StatBlock value={ringless.length} label="RING-LESS" color="red" size="md" />
            </div>
          </div>
        </div>
      </HeroBackground>

      {/* === DEFENDING CHAMPION === */}
      {defending && (
        <section className="px-6 md:px-14 py-14 border-b border-border bg-bg-elevated relative overflow-hidden">
          <Reveal className="relative grid grid-cols-1 lg:grid-cols-[minmax(180px,240px)_1fr_1fr] gap-9 items-center">
            <div className="w-full max-w-[240px] justify-self-center lg:justify-self-start">
              <div className="paper aspect-[4/5] border border-accent-gold/60">
                <CastFigure
                  name={defending.champion}
                  trigger="inview"
                  sequence={['celebrate', 'smug']}
                  className="absolute inset-0 w-full h-full"
                  imgClassName="object-bottom"
                />
              </div>
              <div className="text-center text-[9px] text-accent-gold tracking-[0.3em] font-black mt-3.5">DEFENDING CHAMPION</div>
            </div>
            <div>
              <Eyebrow>{defending.year} · SEASON {defending.year - 2012} · PLAYOFF CHAMPION</Eyebrow>
              <h2 className="text-5xl md:text-6xl font-black leading-[0.95] tracking-normal mt-3 mb-3">{defending.champion.toUpperCase()}</h2>
              <div className="text-lg italic text-ink-secondary font-light mb-5">
                {defending.headline || `Ring number ${ringYears[defending.champion]?.length || 1}. The chase continues.`}
              </div>
              {defending.blurb && <p className="text-sm text-ink-secondary leading-relaxed max-w-md">{defending.blurb}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3.5">
              {defending.reg_season_record && <StatBlock value={defending.reg_season_record} label="REG SEASON" color="gold" size="md" />}
              {defending.points_for && <StatBlock value={defending.points_for} label="POINTS FOR" color="gold" size="md" />}
              {defending.final_margin && <StatBlock value={`+${defending.final_margin}`} label="FINAL MARGIN" color="gold" size="md" />}
              {defending.runner_up && <StatBlock value={defending.runner_up.toUpperCase()} label="FINALS LOSER" color="gold" size="md" />}
              {/* Fallback if no stats available */}
              {!defending.reg_season_record && !defending.points_for && !defending.final_margin && !defending.runner_up && (
                <StatBlock value={ringYears[defending.champion]?.length || 1} label="CAREER RINGS" color="gold" size="md" />
              )}
            </div>
          </Reveal>
        </section>
      )}

      {/* === RING COUNT + DROUGHTS === */}
      <section className="px-6 md:px-14 py-14 border-b border-border bg-bg-deep">
        <Reveal>
          <Eyebrow>RING COUNT</Eyebrow>
          <h2 className="text-display-xs font-black tracking-normal mt-1.5 mb-9">The hardware leaderboard.</h2>
        </Reveal>
        <Reveal delay={80} className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
          {/* Champions */}
          <div className="bg-bg-surface p-6">
            <div className="text-[9px] text-accent-gold tracking-[0.25em] font-extrabold mb-5">THE CHAMPIONS</div>
            {Object.entries(ringCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([name, count], i) => (
                <Link key={name} to={`/managers/${managerSlug(name)}`}
                  className="grid grid-cols-[20px_40px_1fr_60px] md:grid-cols-[24px_50px_1fr_100px] gap-2 md:gap-3.5 items-center py-3 border-b border-border last:border-0 hover:bg-bg-elevated transition-colors">
                  <div className={`text-lg font-black ${i < 2 ? 'text-accent-gold' : 'text-ink-tertiary'}`}>{i + 1}</div>
                  <ManagerAvatar name={name} size="sm" />
                  <div>
                    <div className="text-base font-black">{name.toUpperCase()}</div>
                    <div className="text-[9px] text-ink-tertiary tracking-[0.15em]">{(ringYears[name] || []).join(' · ')}{isFormerMember(name) ? ' · ALUMNUS' : ''}</div>
                  </div>
                  <div className={`text-2xl font-black text-right ${i < 2 ? 'text-accent-gold' : 'text-ink-secondary'}`}>{count}</div>
                </Link>
              ))}
          </div>
          {/* Drought board */}
          <div className="bg-bg-surface p-6 border-l-[3px] border-accent-red">
            <div className="text-[9px] text-accent-red tracking-[0.25em] font-extrabold mb-5">THE DROUGHT BOARD</div>
            {ringless
              .map((name) => ({ name, years: seasonsByManager(name) }))
              .sort((a, b) => b.years - a.years)
              .map(({ name, years }) => (
                <Link key={name} to={`/managers/${managerSlug(name)}`}
                  className="grid grid-cols-[40px_1fr_70px] md:grid-cols-[50px_1fr_100px] gap-2 md:gap-3.5 items-center py-3.5 border-b border-border last:border-0 hover:bg-bg-elevated transition-colors">
                  <ManagerAvatar name={name} size="sm" />
                  <div>
                    <div className="text-base font-black">{name.toUpperCase()}</div>
                    <div className="text-[10px] text-accent-red tracking-[0.1em] italic">{years} seasons of waiting</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-accent-red">{years}</div>
                    <div className="text-[9px] text-ink-tertiary tracking-[0.15em]">YEARS</div>
                  </div>
                </Link>
              ))}
            <div className="text-[11px] text-ink-tertiary tracking-[0.1em] italic mt-5 text-center border-t border-border pt-3.5">
              Combined: <strong className="text-accent-red font-bold">{ringless.reduce((s, n) => s + seasonsByManager(n), 0)} years</strong>, zero rings.
            </div>
          </div>
        </Reveal>
      </section>

      {/* === ALMANAC FOOTNOTES === */}
      {almanacFacts.length > 0 && (
        <section className="px-6 md:px-14 py-14 border-b border-border bg-bg-base">
          <Reveal className="flex flex-col md:flex-row md:justify-between md:items-end gap-3 mb-9">
            <div>
              <Eyebrow color="red">ALMANAC FOOTNOTES</Eyebrow>
              <h2 className="font-display text-display-xs font-black tracking-normal mt-1.5">
                Trophy math with teeth.
              </h2>
            </div>
            <div className="text-[10px] text-ink-tertiary tracking-[0.2em] italic">
              Dynasty notes · drought board · playoff damage
            </div>
          </Reveal>

          <Reveal delay={80} className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
            {almanacFacts.map((fact, i) => (
              <article
                key={`${fact.category}-${i}`}
                className={`bg-bg-surface p-6 border-t-[3px] ${
                  fact.category === 'chokes'
                    ? 'border-accent-red'
                    : fact.category === 'drought_facts'
                      ? 'border-ink-tertiary'
                      : 'border-accent-gold'
                }`}
              >
                <div className={`text-[9px] tracking-[0.25em] font-extrabold mb-3 uppercase ${
                  fact.category === 'chokes' ? 'text-accent-red' : 'text-accent-gold'
                }`}>
                  {fact.label}
                </div>
                <p className="text-sm text-ink-secondary leading-relaxed italic">
                  {fact.text}
                </p>
              </article>
            ))}
          </Reveal>
        </section>
      )}

      {/* === YEAR BY YEAR CHRONICLE === */}
      <section className="px-6 md:px-14 py-14 border-b border-border bg-bg-deep">
        <Reveal>
          <Eyebrow>YEAR BY YEAR</Eyebrow>
          <h2 className="text-display-xs font-black tracking-normal mt-1.5 mb-9">{championships.length} trophies, {championships.length} stories.</h2>
        </Reveal>

        <Reveal delay={80}>
          {visibleYears.map((c) => (
            <Link key={c.year} to={`/managers/${managerSlug(c.champion)}`}
              className="grid grid-cols-1 md:grid-cols-[140px_80px_1fr_200px] gap-6 items-center py-6 border-b border-border last:border-0 hover:bg-bg-elevated transition-colors">
            <div>
              <div className="text-4xl md:text-5xl font-black leading-none text-accent-gold">{c.year}</div>
              <div className="text-[9px] text-ink-tertiary tracking-[0.2em] font-bold mt-1">SEASON {c.year - 2012}</div>
            </div>
            <ManagerAvatar name={c.champion} size="lg" framed />
            <div>
              <div className="text-xl font-black tracking-normal mb-1">{(c.headline || `${c.champion}.`).toUpperCase()}</div>
              <div className="text-sm text-ink-secondary leading-relaxed">{c.blurb || `Champion of ${c.year}.`}</div>
            </div>
            <div className="text-left md:text-right">
              {c.runner_up && (
                <>
                  <div className="text-[9px] text-ink-tertiary tracking-[0.2em] font-bold">DEFEATED</div>
                  <div className="text-sm font-black">{c.runner_up.toUpperCase()}{c.final_margin ? ` · +${c.final_margin}` : ''}</div>
                </>
              )}
              {c.reg_season_record && (
                <>
                  <div className="text-[9px] text-ink-tertiary tracking-[0.2em] font-bold mt-2.5">REG</div>
                  <div className="text-xs font-bold">{c.reg_season_record}{c.points_for ? ` · ${c.points_for} PF` : ''}</div>
                </>
              )}
              {/* When no extra stats available */}
              {!c.runner_up && !c.reg_season_record && c.team && (
                <>
                  <div className="text-[9px] text-ink-tertiary tracking-[0.2em] font-bold">TEAM</div>
                  <div className="text-xs font-bold">{c.team}{c.record ? ` · ${c.record}` : ''}</div>
                </>
              )}
            </div>
            </Link>
          ))}
        </Reveal>

        {!showAll && championships.length > 4 && (
          <div className="text-center pt-5">
            <button onClick={() => setShowAll(true)} className="text-[10px] text-accent-gold tracking-[0.3em] font-extrabold border border-accent-gold px-6 py-3 hover:bg-accent-gold/10">
              ↓ SHOW {championships.length - 4} MORE YEARS
            </button>
          </div>
        )}
      </section>
    </>
  );
}
